import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { addDays, format } from 'date-fns'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { useLinkedHouseholds } from '../../linkedHouseholds/hooks/useLinkedHouseholds'
import { saveWithConflictCheck } from '../../../lib/conflictResolver'

// ── schedules ─────────────────────────────────────────────────
export const useCustodySchedules = () => {
  const { household } = useAuth()
  const { activeLink } = useLinkedHouseholds()
  const qc  = useQueryClient()
  const key = ['custody-schedules', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id && !!activeLink,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('custody_schedules')
        .select(`
          *,
          child:profiles!custody_schedules_child_id_fkey(id, display_name, avatar_url, member_color),
          rules:custody_schedule_rules(*)
        `)
        .or(`household_id.eq.${household.id},linked_household_id.eq.${household.id}`)
        .order('created_at')
      if (error) throw error
      return data ?? []
    },
  })

  const inv = () => qc.invalidateQueries({ queryKey: key })

  const create = useMutation({
    mutationFn: async ({ schedule, rules }) => {
      const { data, error } = await supabase
        .from('custody_schedules')
        .insert({ ...schedule, created_by: household.id })
        .select()
        .single()
      if (error) throw error
      if (rules?.length) {
        const { error: rErr } = await supabase
          .from('custody_schedule_rules')
          .insert(rules.map((r) => ({ ...r, schedule_id: data.id })))
        if (rErr) throw rErr
      }
      return data
    },
    onSuccess: inv,
  })

  const update = useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data, error } = await supabase
        .from('custody_schedules')
        .update(payload)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: inv,
  })

  const remove = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('custody_schedules').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: inv,
  })

  return { ...query, create, update, remove }
}

// ── exceptions ────────────────────────────────────────────────
export const useCustodyExceptions = (scheduleId) => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['custody-exceptions', scheduleId]

  const query = useQuery({
    queryKey: key,
    enabled:  !!scheduleId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('custody_exceptions')
        .select('*')
        .eq('schedule_id', scheduleId)
        .order('exception_date')
      if (error) throw error
      return data ?? []
    },
  })

  const inv = () => {
    qc.invalidateQueries({ queryKey: key })
    qc.invalidateQueries({ queryKey: ['custody-calendar', household?.id] })
  }

  const create = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('custody_exceptions')
        .insert({ ...payload, schedule_id: scheduleId, created_by: user.id })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: inv,
  })

  const remove = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('custody_exceptions').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: inv,
  })

  return { ...query, create, remove }
}

// ── calendar days ─────────────────────────────────────────────
// Returns a map of { [dateStr]: { [scheduleId]: householdId } }
// for a given date range by calling calculate_custody_day per day per schedule
export const useCustodyCalendar = (schedules, startDate, endDate) => {
  const { household } = useAuth()
  const key = ['custody-calendar', household?.id, startDate?.toISOString(), endDate?.toISOString()]

  return useQuery({
    queryKey: key,
    enabled:  !!schedules?.length && !!startDate && !!endDate,
    queryFn:  async () => {
      const days = []
      let cur = startDate
      while (cur <= endDate) {
        days.push(format(cur, 'yyyy-MM-dd'))
        cur = addDays(cur, 1)
      }

      // Batch: for each schedule × day call calculate_custody_day
      const results = {}
      await Promise.all(
        schedules.filter((s) => s.is_active).map(async (schedule) => {
          const promises = days.map(async (dateStr) => {
            const { data, error } = await supabase
              .rpc('calculate_custody_day', {
                p_schedule_id: schedule.id,
                p_check_date:  dateStr,
              })
            return { dateStr, householdId: error ? null : data }
          })
          const resolved = await Promise.all(promises)
          resolved.forEach(({ dateStr, householdId }) => {
            if (!results[dateStr]) results[dateStr] = {}
            results[dateStr][schedule.id] = householdId
          })
        })
      )
      return results
    },
    staleTime: 1000 * 60 * 10,
  })
}

// ── expenses ──────────────────────────────────────────────────
export const useCustodyExpenses = () => {
  const { household, user } = useAuth()
  const { activeLink } = useLinkedHouseholds()
  const qc  = useQueryClient()
  const key = ['custody-expenses', household?.id]

  const linkedHouseholdId = activeLink
    ? (activeLink.primary_household_id === household?.id
        ? activeLink.linked_household_id
        : activeLink.primary_household_id)
    : null

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id && !!linkedHouseholdId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('custody_expenses')
        .select(`
          *,
          child:profiles!custody_expenses_child_id_fkey(id, display_name),
          payer:profiles!custody_expenses_created_by_fkey(id, display_name)
        `)
        .or(`household_id.eq.${household.id},linked_household_id.eq.${household.id}`)
        .order('expense_date', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

  const inv = () => qc.invalidateQueries({ queryKey: key })

  const create = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('custody_expenses')
        .insert({
          ...payload,
          household_id:        household.id,
          linked_household_id: linkedHouseholdId,
          created_by:          user.id,
        })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: inv,
  })

  const update = useMutation({
    mutationFn: async ({ id, localUpdatedAt, ...payload }) => {
      if (localUpdatedAt) {
        const result = await saveWithConflictCheck('custody_expenses', id, payload, localUpdatedAt)
        if (result.conflict) return result
        return result.data
      }
      const { data, error } = await supabase
        .from('custody_expenses')
        .update(payload)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: inv,
  })

  const remove = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('custody_expenses').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: inv,
  })

  const acknowledge = useMutation({
    mutationFn: async (id) => {
      const { data, error } = await supabase
        .from('custody_expenses')
        .update({
          status:           'acknowledged',
          acknowledged_at:  new Date().toISOString(),
          acknowledged_by:  user.id,
        })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: inv,
  })

  const settle = useMutation({
    mutationFn: async (ids) => {
      const { error } = await supabase
        .from('custody_expenses')
        .update({
          status:     'settled',
          settled_at: new Date().toISOString(),
          settled_by: user.id,
        })
        .in('id', ids)
      if (error) throw error
    },
    onSuccess: inv,
  })

  return {
    ...query,
    create,
    update,
    remove,
    acknowledge,
    settle,
    linkedHouseholdId,
  }
}

// ── payments ──────────────────────────────────────────────────
export const useCustodyPayments = () => {
  const { household, user } = useAuth()
  const { activeLink } = useLinkedHouseholds()
  const qc  = useQueryClient()
  const key = ['custody-payments', household?.id]

  const linkedHouseholdId = activeLink
    ? (activeLink.primary_household_id === household?.id
        ? activeLink.linked_household_id
        : activeLink.primary_household_id)
    : null

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id && !!linkedHouseholdId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('custody_payments')
        .select(`*, payer:profiles!custody_payments_created_by_fkey(id, display_name)`)
        .or(`household_id.eq.${household.id},linked_household_id.eq.${household.id}`)
        .order('payment_date', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

  const inv = () => {
    qc.invalidateQueries({ queryKey: key })
    qc.invalidateQueries({ queryKey: ['custody-expenses', household?.id] })
  }

  const create = useMutation({
    mutationFn: async ({ amount, paymentDate, paymentMethod, referenceNote, expenseIds, notes }) => {
      // 1. Create payment record
      const { data: payment, error } = await supabase
        .from('custody_payments')
        .insert({
          household_id:        household.id,
          linked_household_id: linkedHouseholdId,
          created_by:          user.id,
          amount,
          payment_date:        paymentDate,
          payment_method:      paymentMethod || null,
          reference_note:      referenceNote || null,
          expense_ids:         expenseIds ?? [],
          notes:               notes || null,
        })
        .select()
        .single()
      if (error) throw error

      // 2. Settle covered expenses
      if (expenseIds?.length) {
        const { error: sErr } = await supabase
          .from('custody_expenses')
          .update({
            status:     'settled',
            settled_at: new Date().toISOString(),
            settled_by: user.id,
          })
          .in('id', expenseIds)
        if (sErr) throw sErr
      }
      return payment
    },
    onSuccess: inv,
  })

  return { ...query, create, linkedHouseholdId }
}

// ── balance ───────────────────────────────────────────────────
export const useCustodyBalance = (childId = null) => {
  const { household } = useAuth()
  const { activeLink } = useLinkedHouseholds()

  const linkedHouseholdId = activeLink
    ? (activeLink.primary_household_id === household?.id
        ? activeLink.linked_household_id
        : activeLink.primary_household_id)
    : null

  return useQuery({
    queryKey: ['custody-balance', household?.id, linkedHouseholdId, childId],
    enabled:  !!household?.id && !!linkedHouseholdId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .rpc('get_custody_balance', {
          p_household_id:        household.id,
          p_linked_household_id: linkedHouseholdId,
          p_child_id:            childId,
        })
      if (error) throw error
      return data?.[0] ?? { total_owed_to_us: 0, total_owed_by_us: 0, net_balance: 0, unsettled_expense_count: 0 }
    },
  })
}
